import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { Link, useParams } from "react-router";
import { AlertTriangle, ArrowLeft, Info } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import { extractFieldErrors } from "@shared-core/data/validation";
import type {
  ConfirmReplenishmentProposalRequest,
  PreviewedRaiseItem,
  ReplenishmentProposalView,
} from "@shared-core/data/api-types";
import {
  formatCount,
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import {
  ADVISE_REASONS,
  PROPOSAL_STATE_LABELS,
  RAISES_AS_LABELS,
  REPLENISHMENT_MODE_LABELS,
  STAGE_LABELS,
  parsePoundsToPence,
  proposalWaitingLabel,
  triggerArithmetic,
} from "@shared-core/domain/stock-replenishment";
import { paths } from "@shared-core/routing/routes";
import { useCanManageReplenishment } from "../../app/auth/hooks";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../app/components/primitives/card";
import { Checkbox } from "../../app/components/primitives/checkbox";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import { Textarea } from "../../app/components/primitives/textarea";
import { ReplenishmentProgressCard } from "./ReplenishmentProgressCard";
import { RaisePreviewCard, useRaisePreview } from "./RaisePreviewCard";
import { FieldShell } from "./StockFormField";

type Status = "loading" | "ready" | "not_found" | "error";

/** One replenishment proposal: why it triggered, why it waits, and — while
 * open — what a raise would do and confirm (with the owner's nearly-due items
 * ticked in) or dismiss; once raised, its progress to goods-in. */
export function ReplenishmentProposalPage() {
  const { proposalId = "" } = useParams<{ proposalId: string }>();
  const dataAdapter = useDataAdapter();
  const canManage = useCanManageReplenishment();
  const [proposal, setProposal] = useState<ReplenishmentProposalView | null>(
    null,
  );
  const [status, setStatus] = useState<Status>("loading");
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .getReplenishmentProposal(proposalId)
      .then((result) => {
        if (cancelled) return;
        setProposal(result);
        setStatus(result ? "ready" : "not_found");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("stock.replenishment.proposal_load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, proposalId, version]);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Loading proposal…</div>
      </div>
    );
  }

  if (status !== "ready" || proposal == null) {
    return (
      <div className="space-y-4">
        <BackLink />
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-4 border border-border rounded-lg text-muted-foreground"
        >
          <AlertTriangle className="size-4 shrink-0" />
          {status === "not_found"
            ? "This proposal doesn't exist. It may have been mistyped."
            : "Couldn't load this proposal. Please try again."}
        </div>
      </div>
    );
  }

  const reason = proposal.advise_reason
    ? ADVISE_REASONS[proposal.advise_reason]
    : null;
  const madeAnOrder = proposal.state === "raised" && proposal.progress != null;

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl">Reprint {proposal.stock_code}</h1>
            <StateBadge proposal={proposal} />
          </div>
          <p className="text-muted-foreground">
            {proposal.owner_name ?? "Owner name unavailable"} · proposed{" "}
            {formatDateTimeOrDash(proposal.created_at)} ·{" "}
            {REPLENISHMENT_MODE_LABELS[proposal.mode_at_trigger]} mode
          </p>
        </div>
        <Link
          to={paths.stockItemDetail({ stockItemId: proposal.stock_item_id })}
          className="text-sm underline"
        >
          Open stock item
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Why it triggered</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="font-mono tabular-nums">
              {triggerArithmetic(proposal, formatCount)}
            </p>
            <p className="text-muted-foreground">
              {proposal.trigger === "replenish_now"
                ? "Raised by hand with Replenish now."
                : "The sweep found projected stock at or below the re-order level."}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>
              {madeAnOrder ? "What was raised" : "Why it's waiting"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {madeAnOrder && proposal.progress ? (
              <WhatWasRaised proposal={proposal} />
            ) : (
              <>
                <p className="font-medium">
                  {proposal.state === "open" && proposal.raise_blocked_reason
                    ? "Shadow only"
                    : proposalWaitingLabel(proposal)}
                </p>
                {proposal.state === "open" && proposal.raise_blocked_reason && (
                  <p className="text-muted-foreground">
                    {proposal.raise_blocked_reason}
                  </p>
                )}
                {proposal.state === "open" &&
                  !proposal.raise_blocked_reason &&
                  reason && (
                    <p className="text-muted-foreground">{reason.hint}</p>
                  )}
              </>
            )}
            {proposal.artwork && (
              <p className="text-muted-foreground">
                Artwork: {proposal.artwork.revision_label}
                {proposal.artwork.valid_until
                  ? `, valid until ${formatDateOrDash(proposal.artwork.valid_until)}`
                  : ""}
                {proposal.artwork.is_inside_margin && " (inside the margin)"}
                {proposal.artwork.is_expired && " (expired)"}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {proposal.state === "open" ? (
        <ConfirmSection
          key={version}
          proposal={proposal}
          canManage={canManage}
          onDone={() => setVersion((v) => v + 1)}
        />
      ) : madeAnOrder && proposal.progress ? (
        <ReplenishmentProgressCard
          progress={proposal.progress}
          raisedAt={proposal.raised_at}
          raisedByName={proposal.raised_by_name}
        />
      ) : (
        <OutcomeCard
          proposal={proposal}
          canManage={canManage}
          onDone={() => setVersion((v) => v + 1)}
        />
      )}
    </div>
  );
}

function StateBadge({ proposal }: { proposal: ReplenishmentProposalView }) {
  if (proposal.state === "raised" && proposal.stage === "in_stock") {
    return <Badge variant="success">Done</Badge>;
  }
  if (proposal.state === "raised" && proposal.raised_kind === "dry_run") {
    return <Badge variant="neutral">Raised (dry run)</Badge>;
  }
  return (
    <Badge
      variant={
        proposal.state === "open"
          ? "warning"
          : proposal.state === "raised"
            ? "success"
            : proposal.state === "failed"
              ? "error"
              : "neutral"
      }
    >
      {PROPOSAL_STATE_LABELS[proposal.state]}
    </Badge>
  );
}

function WhatWasRaised({ proposal }: { proposal: ReplenishmentProposalView }) {
  const progress = proposal.progress!;
  return (
    <>
      <p>
        Order{" "}
        <Link
          to={paths.orderDetail({ orderNo: progress.order_no })}
          className="underline"
        >
          {progress.order_no}
        </Link>{" "}
        · line {progress.line_ref} · {formatCount(progress.quantity)}
        {proposal.order_kind
          ? ` as a ${RAISES_AS_LABELS[proposal.order_kind].toLowerCase()}`
          : ""}
      </p>
      <p className="text-muted-foreground">
        {proposal.raised_by_name
          ? `Confirmed by ${proposal.raised_by_name}`
          : "Raised by the sweep"}
        , {formatDateTimeOrDash(proposal.raised_at)}.
      </p>
    </>
  );
}

/** What a raise would do, and Confirm / Dismiss: one section so the card and the buttons share the preview. */
function ConfirmSection({
  proposal,
  canManage,
  onDone,
}: {
  proposal: ReplenishmentProposalView;
  canManage: boolean;
  onDone: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [quantity, setQuantity] = useState(String(proposal.proposed_quantity));
  const [included, setIncluded] = useState<
    Record<string, { quantity: string; price: string }>
  >({});
  const [price, setPrice] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const isBlocked = proposal.raise_blocked_reason != null;
  const isDisabled = !canManage || isBlocked || isSaving;

  const ticked = Object.entries(included);
  const request = buildRequest(quantity, ticked, price, orderNumber);
  const preview = useRaisePreview(
    { kind: "proposal", proposalId: proposal.id },
    request.body,
  );

  // What each item still needs, from the preview: the typed price and the customer's order number are asked for
  // only when the owner has no price group / their account wants one.
  const items = preview.preview?.items ?? [];
  const lead = items[0];
  const needsPrice =
    lead?.needs_reason === "price_needed" || errors.price_pence != null;
  const needsOrderNumber =
    lead?.needs_reason === "po_number_needed" ||
    errors.customer_order_number != null;
  const blocker = firstProblem(items);
  const cannotRaise =
    preview.status === "ready" && !preview.preview.can_raise && blocker != null;

  // Preview blockers tied to an input show under it, as a submission's errors do.
  const fieldMessages: Record<string, string> = {};
  for (const item of items) {
    for (const b of item.blockers) {
      if (b.field !== "stock_item_id" && b.field !== "proposal_id") {
        fieldMessages[b.field] ??= b.message;
      }
    }
  }
  const messageFor = (field: string) => errors[field] ?? fieldMessages[field];

  async function run(action: () => Promise<unknown>, failure: string) {
    setIsSaving(true);
    setErrors({});
    setFormError(null);
    try {
      await action();
      onDone();
    } catch (error) {
      console.error("stock.replenishment.proposal_action_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      const fieldErrors = extractFieldErrors(error);
      const map: Record<string, string> = {};
      for (const e of fieldErrors) map[e.field] ??= e.message;
      setErrors(map);
      setFormError(
        fieldErrors.length > 0
          ? `Please fix ${fieldErrors.length === 1 ? "the highlighted field" : `the ${fieldErrors.length} highlighted fields`} and try again.`
          : error instanceof ApiError &&
              (error.status === 409 || error.status === 503)
            ? error.detail
            : failure,
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <RaisePreviewCard state={preview} lastOrder={proposal.last_order} />
      <Card>
        <CardHeader>
          <CardTitle>Confirm</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {isBlocked && (
            <div
              id="confirm-blocked"
              role="note"
              className="flex items-start gap-2 text-sm p-3 rounded-lg border border-sky-300 bg-sky-50 text-sky-900 dark:bg-sky-900/20 dark:border-sky-800 dark:text-sky-200"
            >
              <Info className="size-4 shrink-0 mt-0.5" />
              {proposal.raise_blocked_reason}
            </div>
          )}
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300"
            >
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              {formError}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <FieldShell
              id="confirm-quantity"
              label="Quantity"
              error={messageFor("quantity")}
              hint={`Proposed: the re-order quantity, ${formatCount(proposal.proposed_quantity)}. Change it and the card above refreshes.`}
            >
              {(aria) => (
                <Input
                  id="confirm-quantity"
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  disabled={isDisabled}
                  {...aria}
                />
              )}
            </FieldShell>
            <div className="space-y-1.5 text-sm">
              <div className="text-muted-foreground">Raises as</div>
              <div className="font-medium">
                {proposal.raises_as
                  ? RAISES_AS_LABELS[proposal.raises_as]
                  : "The owner has no billing account yet"}
              </div>
            </div>
            {needsPrice && (
              <FieldShell
                id="confirm-price"
                label="Price to bill, ex VAT"
                error={messageFor("price_pence") ?? request.priceError}
                hint="Recorded as the price a person gave. Only asked for when there is no price group."
              >
                {(aria) => (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">£</span>
                    <Input
                      id="confirm-price"
                      inputMode="decimal"
                      placeholder="e.g. 545.00"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      disabled={isDisabled}
                      {...aria}
                    />
                  </div>
                )}
              </FieldShell>
            )}
            {needsOrderNumber && (
              <FieldShell
                id="confirm-order-number"
                label="Customer order number"
                error={messageFor("customer_order_number")}
                hint="This customer's account needs an order number before an order is raised."
              >
                {(aria) => (
                  <Input
                    id="confirm-order-number"
                    placeholder="e.g. 4500001234"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    disabled={isDisabled}
                    {...aria}
                  />
                )}
              </FieldShell>
            )}
          </div>

          <NearlyDue
            proposal={proposal}
            included={included}
            setIncluded={setIncluded}
            items={items}
            messageFor={messageFor}
            isDisabled={isDisabled}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              disabled={
                isDisabled ||
                !quantity ||
                cannotRaise ||
                request.priceError != null
              }
              aria-describedby={
                isBlocked
                  ? "confirm-blocked"
                  : cannotRaise
                    ? "confirm-cannot-raise"
                    : undefined
              }
              onClick={() =>
                void run(
                  () =>
                    dataAdapter.confirmReplenishmentProposal(
                      proposal.id,
                      request.body ?? { quantity: 0, included_items: [] },
                    ),
                  "Couldn't raise the reprint. Please try again.",
                )
              }
            >
              Confirm &amp; raise
              {ticked.length > 0 && ` (${ticked.length + 1} items)`}
            </Button>
            <DismissPanel
              proposalId={proposal.id}
              disabled={isDisabled}
              onDone={onDone}
            />
            {cannotRaise && blocker && (
              <span
                id="confirm-cannot-raise"
                className="text-xs text-muted-foreground"
              >
                Disabled: {blocker}
              </span>
            )}
            {!cannotRaise && ticked.length > 0 && (
              <span className="text-xs text-muted-foreground">
                One order, {ticked.length + 1} lines.
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

/** The confirm request from the form; ``body`` is null while the quantity is blank, ``priceError`` a typed price
 * that isn't one. */
function buildRequest(
  quantity: string,
  ticked: [string, { quantity: string; price: string }][],
  price: string,
  orderNumber: string,
): {
  body: ConfirmReplenishmentProposalRequest | null;
  priceError: string | undefined;
} {
  const typedPrice = price.trim();
  const pence = typedPrice ? parsePoundsToPence(typedPrice) : null;
  const body: ConfirmReplenishmentProposalRequest = {
    quantity: Number(quantity),
    included_items: ticked.map(([stock_item_id, item]) => ({
      stock_item_id,
      quantity: Number(item.quantity),
      ...(item.price.trim()
        ? { price_pence: parsePoundsToPence(item.price) ?? undefined }
        : {}),
    })),
    ...(pence != null ? { price_pence: pence } : {}),
    ...(orderNumber.trim()
      ? { customer_order_number: orderNumber.trim() }
      : {}),
  };
  return {
    body: quantity.trim() === "" ? null : body,
    priceError:
      typedPrice && pence == null
        ? "Enter the price in pounds, like 545.00."
        : undefined,
  };
}

/** The first thing stopping the raise, in the backend's words, for the disabled button's hint. */
function firstProblem(items: PreviewedRaiseItem[]): string | null {
  for (const item of items) {
    if (item.can_raise) continue;
    const label = items.length > 1 ? `${item.stock_code}: ` : "";
    if (item.blockers.length > 0) return `${label}${item.blockers[0]!.message}`;
    if (item.needs_sentence) return `${label}${item.needs_sentence}`;
    if (item.sourcing_unavailable)
      return `${label}supplier options could not be read just now.`;
  }
  return null;
}

function NearlyDue({
  proposal,
  included,
  setIncluded,
  items,
  messageFor,
  isDisabled,
}: {
  proposal: ReplenishmentProposalView;
  included: Record<string, { quantity: string; price: string }>;
  setIncluded: Dispatch<
    SetStateAction<Record<string, { quantity: string; price: string }>>
  >;
  items: PreviewedRaiseItem[];
  messageFor: (field: string) => string | undefined;
  isDisabled: boolean;
}) {
  const tickedIds = Object.keys(included);
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">
        Also due for {proposal.owner_name ?? "this owner"}
      </legend>
      {proposal.nearly_due_items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          None of the owner's other items is near its re-order level.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            Within 25% of their level, with no proposal of their own. Tick any
            to raise them with this one.
          </p>
          <ul className="space-y-2">
            {proposal.nearly_due_items.map((item) => {
              const isTicked = item.stock_item_id in included;
              const fieldId = `included-${item.stock_item_id}`;
              const position = tickedIds.indexOf(item.stock_item_id);
              const quantityKey = `included_items.${position}.quantity`;
              const priceKey = `included_items.${position}.price_pence`;
              const stockKey = `included_items.${position}.stock_item_id`;
              const previewed = items.find(
                (p) => p.stock_item_id === item.stock_item_id,
              );
              const needsPrice =
                isTicked &&
                (previewed?.needs_reason === "price_needed" ||
                  messageFor(priceKey) != null);
              return (
                <li key={item.stock_item_id} className="space-y-1">
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <Checkbox
                      id={fieldId}
                      checked={isTicked}
                      disabled={isDisabled}
                      onCheckedChange={(checked) =>
                        setIncluded((current) => {
                          const next = { ...current };
                          if (checked === true)
                            next[item.stock_item_id] = {
                              quantity: String(item.reorder_quantity ?? ""),
                              price: "",
                            };
                          else delete next[item.stock_item_id];
                          return next;
                        })
                      }
                    />
                    <Label htmlFor={fieldId} className="min-w-48">
                      {item.stock_code}
                      <span className="ml-2 font-normal text-muted-foreground tabular-nums">
                        projected {formatCount(item.projected)} / level{" "}
                        {formatCount(item.reorder_level)}
                      </span>
                    </Label>
                    {isTicked && (
                      <Input
                        aria-label={`Quantity for ${item.stock_code}`}
                        type="number"
                        min={1}
                        className="w-28"
                        value={included[item.stock_item_id]!.quantity}
                        onChange={(e) =>
                          setIncluded((current) => ({
                            ...current,
                            [item.stock_item_id]: {
                              ...current[item.stock_item_id]!,
                              quantity: e.target.value,
                            },
                          }))
                        }
                        aria-invalid={
                          messageFor(quantityKey) ? true : undefined
                        }
                      />
                    )}
                    {needsPrice && (
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground">£</span>
                        <Input
                          aria-label={`Price to bill for ${item.stock_code}, ex VAT`}
                          inputMode="decimal"
                          placeholder="price"
                          className="w-28"
                          value={included[item.stock_item_id]!.price}
                          onChange={(e) =>
                            setIncluded((current) => ({
                              ...current,
                              [item.stock_item_id]: {
                                ...current[item.stock_item_id]!,
                                price: e.target.value,
                              },
                            }))
                          }
                          aria-invalid={messageFor(priceKey) ? true : undefined}
                        />
                      </span>
                    )}
                  </div>
                  {isTicked &&
                    [quantityKey, priceKey, stockKey].map(
                      (key) =>
                        messageFor(key) && (
                          <p
                            key={key}
                            className="text-sm text-red-600 dark:text-red-400"
                          >
                            {messageFor(key)}
                          </p>
                        ),
                    )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </fieldset>
  );
}

/** Dismiss an open or failed proposal with a reason. The panel owns its own state and error. */
function DismissPanel({
  proposalId,
  disabled,
  onDone,
}: {
  proposalId: string;
  disabled: boolean;
  onDone: () => void;
}) {
  const dataAdapter = useDataAdapter();
  const [isDismissing, setIsDismissing] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function dismiss() {
    setIsSaving(true);
    setError(null);
    try {
      await dataAdapter.dismissReplenishmentProposal(proposalId, reason);
      onDone();
    } catch (failure) {
      console.error("stock.replenishment.proposal_action_failed", {
        status: failure instanceof ApiError ? failure.status : undefined,
      });
      const fieldErrors = extractFieldErrors(failure);
      setError(
        fieldErrors[0]?.message ??
          (failure instanceof ApiError && failure.status === 409
            ? failure.detail
            : "Couldn't dismiss the proposal. Please try again."),
      );
    } finally {
      setIsSaving(false);
    }
  }

  if (!isDismissing) {
    return (
      <Button
        variant="outline"
        disabled={disabled}
        onClick={() => setIsDismissing(true)}
      >
        Dismiss
      </Button>
    );
  }
  return (
    <div className="w-full space-y-2 rounded-lg border border-border p-3">
      <Label htmlFor="dismiss-reason">Why dismiss it?</Label>
      <Textarea
        id="dismiss-reason"
        rows={2}
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby="dismiss-reason-hint"
      />
      <p
        id="dismiss-reason-hint"
        className={
          error
            ? "text-sm text-red-600 dark:text-red-400"
            : "text-xs text-muted-foreground"
        }
      >
        {error ??
          "Required. The sweep proposes again if the item is still below its level, but won't re-raise the ticket for 24 hours."}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setIsDismissing(false);
            setReason("");
            setError(null);
          }}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={disabled || isSaving || !reason.trim()}
          onClick={() => void dismiss()}
        >
          Dismiss proposal
        </Button>
      </div>
    </div>
  );
}

function OutcomeCard({
  proposal,
  canManage,
  onDone,
}: {
  proposal: ReplenishmentProposalView;
  canManage: boolean;
  onDone: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Outcome</CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-1">
        {proposal.state === "raised" && (
          <>
            <p>
              {proposal.raised_kind === "dry_run"
                ? "Dry run: the shadow recorded this reprint; nothing was ordered."
                : "Raised as an order line."}{" "}
              Quantity{" "}
              {formatCount(
                proposal.confirmed_quantity ?? proposal.proposed_quantity,
              )}
              {proposal.order_kind
                ? `, as a ${RAISES_AS_LABELS[proposal.order_kind].toLowerCase()}`
                : ""}
              .
            </p>
            <p className="text-muted-foreground">
              {formatDateTimeOrDash(proposal.raised_at)} by{" "}
              {proposal.raised_by_name ?? "the sweep"}
            </p>
          </>
        )}
        {proposal.state === "dismissed" && (
          <>
            <p>Dismissed: {proposal.dismissed_reason}</p>
            <p className="text-muted-foreground">
              {formatDateTimeOrDash(proposal.dismissed_at)} by{" "}
              {proposal.dismissed_by_name ?? "someone no longer active"}
            </p>
          </>
        )}
        {proposal.state === "failed" && (
          <>
            <p>
              {proposalWaitingLabel(proposal)}
              {proposal.failure_detail ? `: ${proposal.failure_detail}` : ""}.
              The sweep retries it on its next run.
            </p>
            {proposal.stage != null && (
              <p className="text-muted-foreground">
                {STAGE_LABELS[proposal.stage]}
              </p>
            )}
            {canManage && (
              <DismissPanel
                proposalId={proposal.id}
                disabled={false}
                onDone={onDone}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function BackLink() {
  return (
    <Link
      to={paths.stockReplenishment()}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      Replenishment
    </Link>
  );
}
